import { useEffect, useMemo, useState } from "react";
import { HauffeLoader } from "../components/BrandLogo";
import { Layout } from "../components/Layout";
import { Panel } from "../components/Panel";
import { canEditWorkspace, getWorkspaceAccessMessage } from "../lib/adminActions";
import { getCurrentUser } from "../lib/api/authClient";
import {
  fetchContributorDepartment,
  fetchRuntimeOwnerDepartment,
  saveRuntimeDepartmentResponse,
  saveRuntimeDepartmentValueInput,
  type RuntimeDepartmentDetail
} from "../lib/api/runtimeWorkspaceClient";
import { statusLabel } from "../lib/departmentWorkflow";
import { t, useWorkspaceLanguage } from "../lib/i18n";
import { readBilingualText } from "../lib/i18n/bilingualContent";
import { getWorkshopQuestions } from "../lib/workshopQuestions";
import { getPerspectiveDisplayName, valueRoundCanAcceptInput } from "../lib/valueWorkflow";
import type { DepartmentStatus, ResponseStatus, WorkshopAnswers } from "../types";

interface ContributorDepartmentProps {
  departmentId: string;
  audience?: "contributor" | "owner";
}

export function ContributorDepartment({ departmentId, audience = "contributor" }: ContributorDepartmentProps) {
  const language = useWorkspaceLanguage();
  const sharedCopy = t(language).contributorDepartment;
  const copy = language === "en" ? {
    eyebrow: "Department perspective",
    titleFallback: "Department perspective",
    executiveEyebrow: "Executive Management Perspective",
    executiveTitleFallback: "Executive Management Perspective",
    description: (workspaceName: string, isExecutive: boolean) => isExecutive
      ? `${workspaceName}: This view captures the Executive Management perspective.`
      : `${workspaceName}: This view captures only your department's perspective.`,
    mismatch: "The loaded department does not match the opened link.",
    loadError: "Department could not be loaded.",
    savedDraft: "Draft saved.",
    saveDraftError: "Draft could not be saved.",
    submitted: "Your department perspective was submitted.",
    executiveSubmitted: "Your Executive Management perspective was submitted.",
    submitError: "Department perspective could not be submitted.",
    valueSubmitted: "Your value perspective was submitted.",
    valueSaved: "Value perspective saved as draft.",
    valueError: "Value perspective could not be saved.",
    loadingTitle: "Department is loading",
    loadingLabel: "Department perspective is loading",
    errorTitle: "Department could not be loaded",
    backView: audience === "owner" ? "Back to workspace" : "Back to my view",
    lockedTitle: "Workspace is not released for editing",
    workshopStatus: "Workshop status",
    status: "Status",
    answers: "Answers",
    initialPerspective: "Initial perspective",
    valueRoundStatus: "Value Round status",
    trust: (isExecutive: boolean) => isExecutive
      ? "This view captures the Executive Management perspective. The workspace does not evaluate people."
      : "The workspace does not evaluate people. It makes department perspectives visible.",
    valueRound: "Value Round",
    executiveValueRound: "Executive Management Contribution to the Value Round",
    valueQuestion: "Question",
    valueQuestionText: "What matters to you in a valuable customer?",
    executiveValueQuestionText: "What matters from the Executive Management perspective when defining a valuable customer?",
    valueHelp: "Describe from your department's view which characteristics, signals or behaviors make a customer valuable. This is not about evaluating people, but about a shared decision logic for customer quality.",
    executiveValueHelp: "Please describe which characteristics, economic factors, strategic signals or risk points matter from the Executive Management perspective.",
    valueOpenNote: "The Value Round is open. Your contribution is still missing for this perspective.",
    valueClosed: sharedCopy.valueRoundOpensAfterEvaluation,
    valueSubmittedNote: "Your value perspective was submitted.",
    executiveValueSubmittedNote: "Executive Management contribution to the Value Round was submitted.",
    yourAnswer: "Your answer",
    noAnswer: "No answer entered.",
    valueInputTitle: "Contribution to the Value Round",
    executiveValueInputTitle: "Executive Management contribution to the Value Round",
    valueExamples: "Examples: economic contribution, strategic fit, relationship quality, operational effort, decision clarity or recurring demand.",
    valuePlaceholder: "From our department's perspective, a valuable customer is...",
    saveDraft: "Save draft",
    submitFinal: "Submit final",
    saveValueDraft: "Save Value Round contribution",
    submitValueFinal: "Submit Value Round contribution",
    statusPanel: "Status",
    submittedTitle: "Your department perspective was submitted.",
    executiveSubmittedTitle: "Executive Management Perspective was submitted.",
    editingBlockedTitle: "Editing is currently not enabled.",
    submittedBody: "Thank you. Your answers were saved and flow into the shared Customer Value Evaluation.",
    editingBlockedBody: "Editing is currently not enabled. Your existing answers remain protected.",
    assignedOnlyTitle: sharedCopy.assignedOnlyTitle,
    assignedOnlyBody: sharedCopy.assignedOnlyBody,
    assignedOnlyError: sharedCopy.assignedOnlyError,
    backOverview: audience === "owner" ? "Back to workspace" : "Back to my overview",
    questionsTitle: "Workshop questions",
    answerPlaceholder: "Your answer from the department perspective...",
    executiveQuestionsTitle: "Executive Management Perspective",
    executiveAnswerPlaceholder: "Your answer from the Executive Management perspective...",
    executiveQuestionsHelp: "Answer the same core questions from the Executive Management perspective. This perspective complements the department perspectives and brings strategy, profitability, risk, margin, contribution margin and company direction into the Customer Value logic."
  } : {
    eyebrow: "Abteilungsperspektive",
    titleFallback: "Abteilungsperspektive",
    executiveEyebrow: "Geschäftsleitungsperspektive",
    executiveTitleFallback: "Geschäftsleitungsperspektive",
    description: (workspaceName: string, isExecutive: boolean) => isExecutive
      ? `${workspaceName}: Diese Ansicht erfasst die Perspektive der Geschäftsleitung.`
      : `${workspaceName}: Diese Ansicht erfasst ausschließlich die Perspektive Ihrer Abteilung.`,
    mismatch: "Die geladene Abteilung stimmt nicht mit dem geöffneten Link überein.",
    loadError: "Abteilung konnte nicht geladen werden.",
    savedDraft: "Zwischenstand gespeichert.",
    saveDraftError: "Zwischenstand konnte nicht gespeichert werden.",
    submitted: "Ihre Abteilungsperspektive wurde eingereicht.",
    executiveSubmitted: "Ihre Geschäftsleitungsperspektive wurde eingereicht.",
    submitError: "Abteilungsperspektive konnte nicht eingereicht werden.",
    valueSubmitted: "Ihre Value-Perspektive wurde eingereicht.",
    valueSaved: "Value-Perspektive zwischengespeichert.",
    valueError: "Value-Perspektive konnte nicht gespeichert werden.",
    loadingTitle: "Abteilung wird geladen",
    loadingLabel: "Abteilungsperspektive wird geladen",
    errorTitle: "Abteilung konnte nicht geladen werden",
    backView: audience === "owner" ? "Zurück zum Workspace" : "Zurück zu meiner Ansicht",
    lockedTitle: "Workspace nicht zur Bearbeitung freigegeben",
    workshopStatus: "Workshop Status",
    status: "Status",
    answers: "Antworten",
    initialPerspective: "Initiale Perspektive",
    valueRoundStatus: "Status Value-Runde",
    trust: (isExecutive: boolean) => isExecutive
      ? "Diese Ansicht erfasst die Perspektive der Geschäftsleitung. Der Workspace bewertet keine Personen."
      : "Der Workspace bewertet keine Personen. Er macht Abteilungsperspektiven sichtbar.",
    valueRound: "Value-Runde",
    executiveValueRound: "Beitrag der Geschäftsleitung zur Value-Runde",
    valueQuestion: "Frage",
    valueQuestionText: "Was ist euch an einem wertvollen Kunden wichtig?",
    executiveValueQuestionText: "Was ist aus Sicht der Geschäftsleitung an einem wertvollen Kunden wichtig?",
    valueHelp: "Beschreiben Sie aus Sicht Ihrer Abteilung, welche Eigenschaften, Signale oder Verhaltensweisen einen Kunden wertvoll machen. Es geht nicht um Personenbewertung, sondern um eine gemeinsame Entscheidungslogik für Kundenqualität.",
    executiveValueHelp: "Bitte beschreiben Sie frei, welche Eigenschaften, wirtschaftlichen Faktoren, strategischen Signale oder Risikopunkte aus Geschäftsleitungssicht relevant sind.",
    valueOpenNote: "Die Value-Runde ist offen. Für diese Perspektive fehlt noch der Beitrag zur Value-Runde.",
    valueClosed: sharedCopy.valueRoundOpensAfterEvaluation,
    valueSubmittedNote: "Ihre Value-Perspektive wurde eingereicht.",
    executiveValueSubmittedNote: "Beitrag zur Value-Runde eingereicht.",
    yourAnswer: "Ihre Antwort",
    noAnswer: "Keine Antwort eingetragen.",
    valueInputTitle: "Beitrag zur Value-Runde",
    executiveValueInputTitle: "Beitrag der Geschäftsleitung zur Value-Runde",
    valueExamples: "Beispiele: wirtschaftlicher Beitrag, strategische Passung, Beziehungsqualität, operativer Aufwand, Entscheidungsklarheit oder wiederkehrender Bedarf.",
    valuePlaceholder: "Aus Sicht unserer Abteilung ist ein wertvoller Kunde...",
    saveDraft: "Zwischenspeichern",
    submitFinal: "Final einreichen",
    saveValueDraft: "Beitrag zur Value-Runde speichern",
    submitValueFinal: "Beitrag zur Value-Runde einreichen",
    statusPanel: "Status",
    submittedTitle: "Ihre Abteilungsperspektive wurde eingereicht.",
    executiveSubmittedTitle: "Geschäftsleitungsperspektive wurde eingereicht.",
    editingBlockedTitle: "Bearbeitung aktuell nicht freigegeben.",
    submittedBody: "Vielen Dank. Ihre Antworten wurden gespeichert und fließen in die gemeinsame Customer-Value-Auswertung ein.",
    editingBlockedBody: "Die Bearbeitung ist aktuell nicht freigegeben. Ihre bisherigen Antworten bleiben geschützt.",
    assignedOnlyTitle: sharedCopy.assignedOnlyTitle,
    assignedOnlyBody: sharedCopy.assignedOnlyBody,
    assignedOnlyError: sharedCopy.assignedOnlyError,
    backOverview: audience === "owner" ? "Zurück zum Workspace" : "Zurück zu meiner Übersicht",
    questionsTitle: "Workshop-Fragen",
    answerPlaceholder: "Ihre Antwort aus Abteilungssicht...",
    executiveQuestionsTitle: "Geschäftsleitungsperspektive",
    executiveAnswerPlaceholder: "Ihre Antwort aus Sicht der Geschäftsleitung...",
    executiveQuestionsHelp: "Beantworten Sie dieselben Kernfragen aus Sicht der Geschäftsleitung. Diese Perspektive ergänzt die Abteilungsperspektiven und bringt Strategie, Profitabilität, Risiko, Marge, Deckungsbeitrag und Unternehmensrichtung in die Customer-Value-Logik ein."
  };
  const [detail, setDetail] = useState<RuntimeDepartmentDetail | undefined>();
  const [currentUserId, setCurrentUserId] = useState<string | undefined>();
  const [answers, setAnswers] = useState<WorkshopAnswers>({});
  const [responseStatus, setResponseStatus] = useState<ResponseStatus>("draft");
  const [departmentStatus, setDepartmentStatus] = useState<DepartmentStatus>("invited");
  const [valueInput, setValueInput] = useState("");
  const [valueInputStatus, setValueInputStatus] = useState<"draft" | "submitted" | undefined>();
  const [notice, setNotice] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const isExecutivePerspective = detail?.department.perspectiveType === "executive";
  const questions = useMemo(() => getWorkshopQuestions(language), [language]);

  useEffect(() => {
    let isMounted = true;

    async function loadDepartment() {
      setIsLoading(true);
      setError(undefined);
      setNotice(undefined);
      setDetail(undefined);
      setCurrentUserId(undefined);
      setAnswers({});
      setResponseStatus("draft");
      setDepartmentStatus("invited");
      setValueInput("");
      setValueInputStatus(undefined);
      try {
        const [nextDetail, currentUser] = await Promise.all([
          audience === "owner"
            ? fetchRuntimeOwnerDepartment(departmentId)
            : fetchContributorDepartment(departmentId),
          getCurrentUser()
        ]);
        if (!isMounted) return;
        if (nextDetail.department.id !== departmentId) {
          throw new Error(copy.mismatch);
        }
        setDetail(nextDetail);
        setCurrentUserId(currentUser?.id);
        setAnswers(nextDetail.department.answersI18n?.[language] ?? nextDetail.department.answers ?? {});
        setResponseStatus((nextDetail.department.responseStatus as ResponseStatus | undefined) ?? "draft");
        setDepartmentStatus(nextDetail.department.status as DepartmentStatus);
        setValueInput(readBilingualText(nextDetail.department.valueInput?.rawInputI18n ?? nextDetail.department.valueInput?.rawInput, language));
        setValueInputStatus(nextDetail.department.valueInput?.status);
      } catch (caught) {
        if (isMounted) setError(caught instanceof Error ? caught.message : copy.loadError);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    void loadDepartment();

    return () => {
      isMounted = false;
    };
  }, [audience, departmentId, copy.loadError, copy.mismatch, language]);

  useEffect(() => {
    if (!detail || typeof window === "undefined" || window.location.hash !== "#value-round") return;

    const scrollToValueRound = () => {
      const target = document.getElementById("value-round");
      target?.scrollIntoView({ block: "start", behavior: "smooth" });
      target?.focus({ preventScroll: true });
    };

    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => window.setTimeout(scrollToValueRound, 80));
    } else {
      window.setTimeout(scrollToValueRound, 80);
    }
  }, [detail]);

  const answeredCount = useMemo(
    () => questions.filter((question) => answers[question.id]?.trim()).length,
    [answers, questions]
  );

  const accessMessage = detail ? getWorkspaceAccessMessage(detail.workspace.status, language) : undefined;
  const isSubmitted = responseStatus === "submitted" || departmentStatus === "submitted";
  const isAssignedParticipant = Boolean(detail?.department.participantUserId && currentUserId && detail.department.participantUserId === currentUserId);
  const isOwnerViewingContributorPerspective = audience === "owner" && Boolean(detail?.department.participantUserId) && !isAssignedParticipant;
  const initialResponseLockedByProcess = detail?.process?.status ? detail.process.status !== "department_input_open" : false;
  const canEditInitialDepartmentResponse = Boolean(detail && isAssignedParticipant && canEditWorkspace(detail.workspace.status) && !initialResponseLockedByProcess);
  const canSubmitInitialDepartmentResponse = canEditInitialDepartmentResponse;
  const isValueRoundRoute = typeof window !== "undefined" && window.location.hash === "#value-round";
  const canAnswerValueRound = valueRoundCanAcceptInput(detail?.process?.status);
  const valueRoundLockedByProcess = detail?.process?.status === "calculation_confirmed";
  const canEditValueRoundInput = Boolean(detail && isAssignedParticipant && canAnswerValueRound && !valueRoundLockedByProcess);
  const shouldShowValueRoundSection = isValueRoundRoute || canAnswerValueRound || valueInputStatus === "draft" || valueInputStatus === "submitted";
  const answeredQuestions = questions.filter((question) => answers[question.id]?.trim());

  function updateAnswer(questionId: string, value: string) {
    setAnswers((currentAnswers) => ({
      ...currentAnswers,
      [questionId]: value
    }));
  }

  async function saveDraft() {
    if (!detail) return;
    if (!canEditInitialDepartmentResponse) {
      setError(copy.assignedOnlyError);
      return;
    }
    setIsSaving(true);
    setNotice(undefined);
    setError(undefined);
    try {
      const response = await saveRuntimeDepartmentResponse(detail.department.id, answers, false, language);
      setResponseStatus(response.response.responseStatus as ResponseStatus);
      setDepartmentStatus(response.response.responseStatus === "submitted" ? "submitted" : "started");
      setNotice(copy.savedDraft);
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "";
      setError(message === "Forbidden" ? copy.assignedOnlyError : message || copy.saveDraftError);
    } finally {
      setIsSaving(false);
    }
  }

  async function submitFinal() {
    if (!detail) return;
    if (!canSubmitInitialDepartmentResponse) {
      setError(copy.assignedOnlyError);
      return;
    }
    setIsSaving(true);
    setNotice(undefined);
    setError(undefined);
    try {
      const response = await saveRuntimeDepartmentResponse(detail.department.id, answers, true, language);
      setResponseStatus(response.response.responseStatus as ResponseStatus);
      setDepartmentStatus("submitted");
      setNotice(copy.submitted);
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "";
      setError(message === "Forbidden" ? copy.assignedOnlyError : message || copy.submitError);
    } finally {
      setIsSaving(false);
    }
  }

  async function saveValueInput(submit: boolean) {
    if (!detail) return;
    if (!canEditValueRoundInput) {
      setError(copy.assignedOnlyError);
      return;
    }
    setIsSaving(true);
    setNotice(undefined);
    setError(undefined);
    try {
      const response = await saveRuntimeDepartmentValueInput(detail.department.id, valueInput, submit, language);
      setValueInput(response.valueInput?.rawInput ?? valueInput);
      setValueInputStatus(response.valueInput?.status);
      setNotice(submit ? copy.valueSubmitted : copy.valueSaved);
      if (response.process && detail) {
        setDetail({ ...detail, process: response.process });
      }
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "";
      setError(message === "Forbidden" ? copy.assignedOnlyError : message || copy.valueError);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Layout
      eyebrow={isExecutivePerspective ? copy.executiveEyebrow : copy.eyebrow}
      title={detail ? getPerspectiveDisplayName({ ...detail.department, name: readBilingualText(detail.department.nameI18n ?? detail.department.name, language) }, language) : (isExecutivePerspective ? copy.executiveTitleFallback : copy.titleFallback)}
      description={copy.description(detail?.workspace.name ?? "Customer Value Workspace", isExecutivePerspective)}
    >
      {isLoading ? (
        <Panel title={copy.loadingTitle}>
          <HauffeLoader label={copy.loadingLabel} />
        </Panel>
      ) : null}
      {error ? (
        <Panel title={copy.errorTitle} variant="accent">
          <p className="form-error" role="alert">{error}</p>
          <div className="hero-actions">
            <a className="button button-primary" href={audience === "owner" ? "/workspace" : "/contributor"}>
              {copy.backView}
            </a>
          </div>
        </Panel>
      ) : null}
      {detail ? (
        <>
          {accessMessage ? (
            <Panel title={copy.lockedTitle} variant="accent">
              <p>{accessMessage}</p>
            </Panel>
          ) : null}

          <Panel title={copy.workshopStatus} variant="accent">
            <p>
              {copy.initialPerspective}: <span className={`status-badge status-${departmentStatus}`}>{statusLabel(departmentStatus, language)}</span>
            </p>
            <p>
              {copy.answers}: {answeredCount}/{questions.length}
            </p>
            <p>
              {copy.valueRoundStatus}: <span className={`status-badge status-${valueInputStatus ?? "open"}`}>{valueInputStatus === "submitted" ? copy.valueSubmittedNote : valueInputStatus === "draft" ? copy.valueSaved : canAnswerValueRound ? copy.valueOpenNote : copy.valueClosed}</span>
            </p>
            <p className="muted">{copy.trust(isExecutivePerspective)}</p>
          </Panel>

          {notice ? (
            <Panel title={copy.statusPanel} variant="soft">
              <p>{notice}</p>
            </Panel>
          ) : null}

          {isValueRoundRoute ? null : !canEditWorkspace(detail.workspace.status) || !canEditInitialDepartmentResponse ? (
            <Panel title={initialResponseLockedByProcess || isSubmitted ? (isExecutivePerspective ? copy.executiveSubmittedTitle : copy.submittedTitle) : isOwnerViewingContributorPerspective ? copy.assignedOnlyTitle : copy.editingBlockedTitle} variant="accent">
              {initialResponseLockedByProcess || isSubmitted ? (
                <p>{copy.submittedBody}</p>
              ) : isOwnerViewingContributorPerspective ? (
                <p>{copy.assignedOnlyBody}</p>
              ) : (
                <p>{copy.editingBlockedBody}</p>
              )}
              <div className="hero-actions">
                <a className="button button-primary" href={audience === "owner" ? "/workspace" : "/contributor"}>
                  {copy.backOverview}
                </a>
              </div>
              {answeredQuestions.length > 0 ? (
                <div className="question-flow">
                  {answeredQuestions.map((question, index) => (
                    <article key={question.id} className="question-card">
                      <span className="question-number">{String(index + 1).padStart(2, "0")}</span>
                      <strong>{question.label}</strong>
                      <p>{answers[question.id]}</p>
                    </article>
                  ))}
                </div>
              ) : null}
            </Panel>
          ) : (
            <Panel title={isExecutivePerspective ? copy.executiveQuestionsTitle : copy.questionsTitle}>
              {isExecutivePerspective ? <p className="form-note">{copy.executiveQuestionsHelp}</p> : null}
              <form className="question-flow">
                {questions.map((question, index) => (
                  <label key={question.id} className="question-card">
                    <span className="question-number">{String(index + 1).padStart(2, "0")}</span>
                    <strong>{question.label}</strong>
                    <small>{question.help}</small>
                    <textarea
                      value={answers[question.id] ?? ""}
                      onChange={(event) => updateAnswer(question.id, event.target.value)}
                      rows={5}
                      placeholder={isExecutivePerspective ? copy.executiveAnswerPlaceholder : copy.answerPlaceholder}
                    />
                  </label>
                ))}
                <div className="form-actions">
                  <button className="button button-secondary" type="button" onClick={saveDraft} disabled={isSaving}>
                    {copy.saveDraft}
                  </button>
                  <button className="button button-primary" type="button" onClick={submitFinal} disabled={isSaving}>
                    {copy.submitFinal}
                  </button>
                </div>
              </form>
            </Panel>
          )}

          {shouldShowValueRoundSection ? (
            <div id="value-round" tabIndex={-1}>
              <Panel title={isExecutivePerspective ? copy.executiveValueRound : copy.valueRound} variant="accent">
                <p>
                  {copy.valueQuestion}: <strong>{isExecutivePerspective ? copy.executiveValueQuestionText : copy.valueQuestionText}</strong>
                </p>
                <p className="muted">{isExecutivePerspective ? copy.executiveValueHelp : copy.valueHelp}</p>
                {!canAnswerValueRound && valueInputStatus !== "submitted" ? (
                  <p className="form-note">
                    {copy.valueClosed}
                  </p>
                ) : isOwnerViewingContributorPerspective && valueInputStatus !== "submitted" ? (
                  <p className="form-note">
                    {copy.assignedOnlyBody}
                  </p>
                ) : valueInputStatus === "submitted" && !canEditValueRoundInput ? (
                  <>
                    <p className="form-note">{isExecutivePerspective ? copy.executiveValueSubmittedNote : copy.valueSubmittedNote}</p>
                    <div className="question-card">
                      <strong>{copy.yourAnswer}</strong>
                      <p>{valueInput || copy.noAnswer}</p>
                    </div>
                  </>
                ) : (
                  <form className="question-flow">
                    <label className="question-card">
                      <strong>{isExecutivePerspective ? copy.executiveValueInputTitle : copy.valueInputTitle}</strong>
                      <small>{copy.valueExamples}</small>
                      <textarea
                        value={valueInput}
                        onChange={(event) => setValueInput(event.target.value)}
                        rows={7}
                        placeholder={isExecutivePerspective ? copy.executiveValueQuestionText : copy.valuePlaceholder}
                      />
                    </label>
                    <div className="form-actions">
                      <button className="button button-secondary" type="button" onClick={() => void saveValueInput(false)} disabled={isSaving}>
                        {copy.saveValueDraft}
                      </button>
                      <button className="button button-primary" type="button" onClick={() => void saveValueInput(true)} disabled={isSaving}>
                        {copy.submitValueFinal}
                      </button>
                    </div>
                  </form>
                )}
              </Panel>
            </div>
          ) : null}
        </>
      ) : null}
    </Layout>
  );
}
